import { StandardEvents } from '@shopify/events';

class ProductEnquiry extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.generation = 0;
    const { signal } = this.controller;
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.onProductSelect, { signal });
    this.addEventListener('click', this.onClick, { signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  onClick = (event) => {
    if (event.target.closest('[data-enquiry-link]')?.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
    }
  };

  onProductSelect = async (event) => {
    const sourceCard = event.target.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;
    const sourcePicker = event.target.closest('variant-picker');
    if (sourcePicker?.dataset.productId !== this.dataset.productId) return;
    const generation = ++this.generation;
    const link = this.querySelector('[data-enquiry-link]');
    if (!link || !event.promise) return;
    link.setAttribute('aria-disabled', 'true');
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.generation) return;
      const next = detail?.html?.querySelector('ooa-product-enquiry');
      const nextLink = next?.querySelector('[data-enquiry-link]');
      const nextDescription = next?.querySelector('[data-enquiry-description]');
      if (!nextLink || !nextDescription || !detail.resource) throw new Error('Enquiry context unavailable');
      link.href = nextLink.href;
      link.textContent = nextLink.textContent;
      this.querySelector('[data-enquiry-description]').textContent = nextDescription.textContent;
      this.dataset.productId = next.dataset.productId;
    } catch {
      if (!this.isConnected || generation !== this.generation) return;
      const productUrl = new URL(window.location.href);
      const enquiryUrl = new URL(link.href);
      const variantId = productUrl.searchParams.get('variant');
      const variantQuery = variantId && /^\d+$/.test(variantId) ? '?variant=' + variantId : '';
      enquiryUrl.searchParams.set('product_url', productUrl.origin + productUrl.pathname + variantQuery);
      if (sourcePicker?.dataset.productTitle) enquiryUrl.searchParams.set('product', sourcePicker.dataset.productTitle);
      enquiryUrl.searchParams.delete('variant');
      enquiryUrl.searchParams.set('enquiry', 'product');
      link.href = enquiryUrl.href;
      link.textContent = this.dataset.linkLabel + ' →';
      this.querySelector('[data-enquiry-description]').textContent = this.dataset.description;
    } finally {
      if (generation === this.generation) link.removeAttribute('aria-disabled');
    }
  };
}

class EnquiryContext extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialised === 'true' || this.dataset.canPrefill !== 'true') return;
    this.dataset.initialised = 'true';
    const body = this.closest('form')?.querySelector('textarea[name="contact[body]"]');
    if (!body || body.value.trim()) return;
    const params = new URLSearchParams(window.location.search);
    const product = (params.get('product') || '').trim().slice(0,400);
    const variant = (params.get('variant') || '').trim().slice(0,250);
    const rawUrl = (params.get('product_url') || '').slice(0,2000);
    if (!product || !rawUrl) return;
    let productUrl;
    try {
      productUrl = new URL(rawUrl, window.location.origin);
    } catch {
      return;
    }
    const storeOrigin = new URL(this.dataset.storeUrl, window.location.origin).origin;
    if (![storeOrigin, window.location.origin].includes(productUrl.origin) || !/^\/(?:[a-z]{2}(?:-[a-zA-Z]{2})?\/)?products\/[^/]+\/?$/.test(productUrl.pathname)) return;
    const variantId = productUrl.searchParams.get('variant');
    productUrl.search = '';
    productUrl.hash = '';
    if (variantId && /^\d+$/.test(variantId)) productUrl.searchParams.set('variant', variantId);
    const lines = [`${this.dataset.productLabel}: ${product}`];
    if (variant && variant !== 'Default Title') lines.push(`${this.dataset.variantLabel}: ${variant}`);
    lines.push(`${this.dataset.linkLabel}: ${productUrl.href}`, '');
    lines.push(params.get('enquiry') === 'availability' ? this.dataset.availabilityQuestion : `${this.dataset.questionLabel}:`, '');
    body.value = lines.join('\n');
    const returnLink = this.querySelector('[data-enquiry-return]');
    returnLink.href = productUrl.pathname + productUrl.search;
    this.hidden = false;
  }
}

if (!customElements.get('ooa-product-enquiry')) customElements.define('ooa-product-enquiry', ProductEnquiry);
if (!customElements.get('ooa-enquiry-context')) customElements.define('ooa-enquiry-context', EnquiryContext);
